/**
 * Workshop showcase for TodoFrame, MusicFrame, and LinkPreviewFrame.
 *
 * Realistic mocks plus edge cases: all-done todos, empty items,
 * streaming and error states.
 * Registered in workshop-routes.tsx at /workshop/shapes/task-music-link.
 */

import { useState } from "react";
import { TodoFrame } from "@/workshop/components/shapes/todo-frame/todo-frame";
import { MusicFrame } from "@/workshop/components/shapes/music-frame/music-frame";
import { LinkPreviewFrame } from "@/workshop/components/shapes/link-preview-frame/link-preview-frame";

// ─── Mock data ────────────────────────────────────────────────────────────────

const NYC_TODOS = [
  { id: "t1", text: "Book flights to NYC", detail: "Fri evening · JFK · 2 seats", priority: "urgent" as const },
  { id: "t2", text: "Confirm hotel near Williamsburg", detail: "Wythe · 2 nights", priority: "high" as const },
  { id: "t3", text: "Find rooftop bar for Thursday", detail: "Somewhere with a skyline view", priority: "medium" as const },
  { id: "t4", text: "Pack chargers + passport", priority: "low" as const },
];

const ALL_DONE_TODOS = [
  { id: "d1", text: "Book flights to NYC", detail: "Confirmed · Fri 18:40", priority: "urgent" as const, done: true },
  { id: "d2", text: "Confirm hotel", detail: "Wythe · confirmation #4821", priority: "high" as const, done: true },
  { id: "d3", text: "Pack chargers + passport", priority: "low" as const, done: true },
];

// ─── Section helper ───────────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-10" data-testid={`task-music-link-section-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <h2 className="mb-4 text-sm font-semibold text-white/80">{title}</h2>
      <div className="flex flex-wrap items-start gap-6">{children}</div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function TaskMusicLinkFramesPage() {
  const [lastToggle, setLastToggle] = useState<string | null>(null);

  return (
    <div className="workshop-canvas-bg min-h-full p-8" data-testid="task-music-link-frames-page">
      <style>{`.workshop-canvas-bg{background-color:#0f1115;background-image:linear-gradient(0deg,transparent,transparent),linear-gradient(135deg,#2b2f36 0%,#343a42 40%,#3d424a 70%,#3b2f2a 100%);position:relative}.workshop-canvas-bg::before{content:"";position:absolute;inset:0;z-index:0;pointer-events:none;opacity:0.1;background:radial-gradient(circle at 22% 18%,rgba(156,132,255,0.18),transparent 58%),radial-gradient(circle at 72% 70%,rgba(111,168,198,0.12),transparent 60%)}.workshop-canvas-bg>*{position:relative;z-index:1}`}</style>
      <div className="mb-8">
        <h1 className="text-lg font-bold text-white">Todo · Music · Link Preview</h1>
        <p className="mt-1 text-sm text-white/40">
          Checklist, now-playing row, and URL unfurl frames. Checkboxes toggle
          locally.
        </p>
        {lastToggle && (
          <p className="mt-2 text-xs text-white/50" data-testid="task-music-link-frames-last-action">
            Toggled {lastToggle}
          </p>
        )}
      </div>

      <Section title="Happy path">
        <div className="w-[320px]">
          <TodoFrame
            data-testid="todo-frame-demo"
            items={NYC_TODOS}
            status="done"
            onToggle={setLastToggle}
          />
        </div>
        <div className="w-[320px]">
          <MusicFrame
            data-testid="music-frame-demo"
            embedId="4uLU6hMCjMI75M1A2tKUQ"
            track="Never Gonna Give You Up"
            artist="Rick Astley"
          />
        </div>
        <div className="w-[340px]">
          <LinkPreviewFrame data-testid="link-preview-frame-demo" />
        </div>
      </Section>

      <Section title="Todo edge cases">
        <div className="w-[320px]">
          <TodoFrame
            data-testid="todo-frame-all-done"
            items={ALL_DONE_TODOS}
            status="done"
            onToggle={setLastToggle}
          />
        </div>
        <div className="w-[320px]">
          <TodoFrame data-testid="todo-frame-empty" items={[]} status="done" />
        </div>
        <div className="w-[320px]">
          <TodoFrame data-testid="todo-frame-streaming" items={[]} status="streaming" />
        </div>
        <div className="w-[320px]">
          <TodoFrame data-testid="todo-frame-error" items={NYC_TODOS} status="error" />
        </div>
      </Section>

      <Section title="Music edge cases">
        <div className="w-[320px]">
          <MusicFrame
            data-testid="music-frame-long"
            track="We Gotta Get Out of This Place (Remastered 2026 Deluxe Edition)"
            artist="The Animals"
            album="The Very Best of the Animals"
            position={3722}
            duration={5400}
            context="Road-trip mix · 48 songs"
            status="done"
          />
        </div>
        <div className="w-[320px]">
          <MusicFrame data-testid="music-frame-streaming" status="streaming" />
        </div>
        <div className="w-[320px]">
          <MusicFrame data-testid="music-frame-error" status="error" />
        </div>
      </Section>

      <Section title="Link preview edge cases">
        <div className="w-[340px]">
          <LinkPreviewFrame
            data-testid="link-preview-frame-single"
            url="https://example.com/flight/nyc-fri"
            title="Fri 18:40 JFK — nonstop, 2 seats left at this price"
            description="Includes carry-on. Free cancellation for 24 hours."
            status="done"
          />
        </div>
        <div className="w-[340px]">
          <LinkPreviewFrame data-testid="link-preview-frame-streaming" status="streaming" />
        </div>
        <div className="w-[340px]">
          <LinkPreviewFrame data-testid="link-preview-frame-error" status="error" />
        </div>
      </Section>
    </div>
  );
}
